/**
 * Seedable, deterministic PRNG for the simulation core.
 *
 * Everything in `sim-core` that needs randomness MUST go through one of these
 * generators — never `Math.random()`. The same seed must produce the exact same
 * stream in Node and in the browser (both V8), which is what makes the
 * server-authoritative replay and provably-fair verification possible.
 *
 * Algorithm: mulberry32 — 32-bit state, fast, good distribution for game use,
 * trivially serializable (the whole state is a single uint32).
 */
export class Rng {
  private s: number;

  constructor(seed: number) {
    // Force to uint32 so behaviour is identical regardless of how the seed
    // was produced (negative / float / >2^32 inputs are normalised).
    this.s = seed >>> 0;
  }

  /** Float in [0, 1) — the mulberry32 step. */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Integer in [min, max] inclusive. */
  intRange(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }

  /** `true` with probability `p`. */
  chance(p: number): boolean {
    return this.next() < p;
  }

  /** Current internal state — serialize for snapshots / resume. */
  getState(): number {
    return this.s >>> 0;
  }

  /** Restore a previously captured state. */
  setState(state: number): void {
    this.s = state >>> 0;
  }

  /** Independent copy at the current position (does not advance the original). */
  clone(): Rng {
    const r = new Rng(0);
    r.s = this.s;
    return r;
  }
}

/**
 * Deterministic string -> uint32 hash (xmur3-based). Used to derive a numeric
 * seed from a textual seed (e.g. a player-supplied clientSeed) when a full
 * HMAC derivation is not available. The authoritative provably-fair derivation
 * lives server-side; this keeps client/offline paths reproducible.
 */
export function seedFromString(str: string): number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return (h ^= h >>> 16) >>> 0;
}
